import { jsPDF } from "jspdf";

/**
 * ============================================================================
 * GST NUMBER / GSTIN CONFIGURATION
 * ----------------------------------------------------------------------------
 * Replace this placeholder value with your original GST number.
 * Typical Indian GSTIN format: 15 alphanumeric characters (e.g. 29AAAAA0000A1Z5)
 * ============================================================================
 */
export const ORGANIZER_GSTIN = "29AABAV9979M1"; // <-- REPLACE WITH YOUR ORIGINAL GST NUMBER

/**
 * Generates and downloads an official registration receipt PDF for IVEC 2027 delegates.
 * 
 * @param {Object} userData       - Delegate user information
 * @param {Object} paymentData    - Payment and transaction record
 */
export const generateReceiptPdf = (userData, paymentData) => {
    if (!paymentData) return;

    const gstNumber = paymentData?.gstNumber || userData?.gstNumber || ORGANIZER_GSTIN;

    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
    });

    const isForeign = userData?.delegateType === "foreign" || paymentData?.currency === "USD";
    const currency = paymentData?.currency || (isForeign ? "USD" : "INR");
    const symbol = currency === "USD" ? "$" : "Rs.";

    const formatNum = (num) => {
        if (num === undefined || num === null || isNaN(num)) return "0.00";
        return Number(num).toLocaleString(isForeign ? "en-US" : "en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    // Original package names as selected by user in the UI
    const getTierHumanName = (tier) => {
        switch (tier?.toLowerCase()) {
            case "student":
                return "Conference only for students";
            case "retiree":
                return "Conference only for retirees";
            case "minicourse":
                return "Mini-Course Only";
            case "singleday":
                return "Single-Day Entry Pass";
            case "fullaccess":
            case "normal":
            case "regular":
            default:
                return "Conference only";
        }
    };

    const receiptNo = paymentData.razorpayPaymentId
        ? `RCPT-${paymentData.razorpayPaymentId.replace("pay_", "").toUpperCase()}`
        : `RCPT-${(paymentData.razorpayOrderId || "IVEC").replace("order_", "").toUpperCase()}`;

    const dateFormatted = paymentData.verifiedDate
        ? new Date(paymentData.verifiedDate).toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
        })
        : new Date().toLocaleDateString("en-IN", { dateStyle: "medium" });

    // -------------------------------------------------------------
    // 1. TOP HEADER BANNER (Navy & Gold Accent)
    // -------------------------------------------------------------
    doc.setFillColor(11, 41, 84); // Navy
    doc.rect(0, 0, 210, 38, "F");

    doc.setFillColor(217, 119, 6); // Amber / Gold Accent Line
    doc.rect(0, 38, 210, 2.5, "F");

    // Header Title - IVEC 2027
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text("INTERNATIONAL VACUUM ELECTRONICS CONFERENCE (IVEC 2027)", 105, 13, { align: "center" });

    // Subtitle - Official Registration Receipt
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(251, 191, 36); // Light Gold
    doc.text("OFFICIAL REGISTRATION RECEIPT", 105, 21, { align: "center" });

    // Organizer details & Official Domain
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(226, 232, 240);
    doc.text(
        "Jointly Organized by MTRDC (DRDO), Bengaluru, India & IEEE Electron Devices Society (EDS)",
        105,
        28,
        { align: "center" }
    );
    doc.text("Venue: Bengaluru, Karnataka, India  |  Website: https://ivec2027.org.in/", 105, 33, { align: "center" });

    // -------------------------------------------------------------
    // 2. RECEIPT META & STATUS BAR
    // -------------------------------------------------------------
    let y = 47;

    // Receipt Reference No
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text("RECEIPT NO:", 14, y);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(11, 41, 84);
    doc.text(receiptNo, 42, y);

    // Issue Date
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("ISSUED ON:", 14, y + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(dateFormatted, 42, y + 5.5);

    // GSTIN (Organizer GST Number)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("GSTIN:", 14, y + 11);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(gstNumber, 42, y + 11);

    // Status Pill (Top Right)
    doc.setFillColor(236, 253, 245); // Emerald-50
    doc.setDrawColor(16, 185, 129);  // Emerald-500
    doc.roundedRect(138, y - 4, 58, 12, 3, 3, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(4, 120, 87); // Emerald-700
    doc.text("PAYMENT CONFIRMED", 167, y + 2.5, { align: "center" });
    doc.setFontSize(6.5);
    doc.text("Verified & Reconciled", 167, y + 6.5, { align: "center" });

    // -------------------------------------------------------------
    // 3. SECTION 1: DELEGATE PARTICIPANT DETAILS
    // -------------------------------------------------------------
    y = 65;
    doc.setFillColor(241, 245, 249);
    doc.rect(14, y, 182, 7, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(11, 41, 84);
    doc.text("1. REGISTERED DELEGATE INFORMATION", 18, y + 5);

    y += 7;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.rect(14, y, 182, 38, "FD");

    const delegateName = userData?.displayName || userData?.userName || "Participant";
    const delegateEmail = userData?.email || "—";
    const delegatePhone = `${userData?.countryCode ? userData.countryCode + " " : ""}${userData?.mobile || "—"}`;
    const delegateTypeStr = isForeign ? "Foreign Delegate (International)" : "Indian Delegate (Domestic)";
    const affiliation = userData?.affiliation || "Not Specified";
    const idDocLabel = isForeign ? "Passport No:" : "Aadhaar No:";
    const idDocValue = isForeign
        ? userData?.passportNo || "N/A"
        : userData?.aadhaarNo
            ? `XXXX-XXXX-${userData.aadhaarNo.slice(-4)}`
            : "N/A";
    const ieeeNo = paymentData?.ieeeMemberNo || userData?.ieeeMemberNo || "Not Enrolled";

    // Column 1 (Left: x=18)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Delegate Name:", 18, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text(delegateName, 48, y + 6);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Email Address:", 18, y + 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(delegateEmail, 48, y + 13);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Contact No:", 18, y + 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(delegatePhone, 48, y + 20);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Affiliation:", 18, y + 27);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(affiliation.length > 35 ? affiliation.substring(0, 35) + "..." : affiliation, 48, y + 27);

    // Column 2 (Right: x=112)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Delegate Type:", 112, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(11, 41, 84);
    doc.text(delegateTypeStr, 142, y + 6);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(idDocLabel, 112, y + 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(idDocValue, 142, y + 13);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("IEEE Member ID:", 112, y + 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    if (paymentData?.discountAmount > 0) {
        doc.setTextColor(4, 120, 87);
    } else {
        doc.setTextColor(15, 23, 42);
    }
    doc.text(ieeeNo + (paymentData?.discountAmount > 0 ? " (10% Off Applied)" : ""), 142, y + 20);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Country:", 112, y + 27);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(userData?.nationality || (isForeign ? "International" : "India"), 142, y + 27);

    // -------------------------------------------------------------
    // 4. SECTION 2: REGISTRATION PACKAGE & ITEMIZATION TABLE
    // -------------------------------------------------------------
    y += 45;
    doc.setFillColor(241, 245, 249);
    doc.rect(14, y, 182, 7, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(11, 41, 84);
    doc.text("2. REGISTRATION PACKAGE & BILLING ITEMIZATION", 18, y + 5);

    y += 7;

    // Table Header
    doc.setFillColor(226, 232, 240);
    doc.rect(14, y, 182, 7, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(30, 41, 59);
    doc.text("SL", 18, y + 4.8);
    doc.text("PACKAGE / ITEM DETAILS", 30, y + 4.8);
    doc.text("CATEGORY", 125, y + 4.8);
    doc.text("AMOUNT (" + currency + ")", 175, y + 4.8, { align: "right" });

    y += 7;

    // Item 1: Base Conference Pass (Original selected name)
    let rowCount = 1;
    const baseFee = paymentData.baseAmount || 0;

    doc.setFillColor(255, 255, 255);
    doc.rect(14, y, 182, 8.5, "F");
    doc.setDrawColor(241, 245, 249);
    doc.line(14, y + 8.5, 196, y + 8.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(String(rowCount++), 18, y + 5.5);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 23, 42);
    doc.text(getTierHumanName(paymentData.feeTier), 30, y + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    doc.text(isForeign ? "International" : "Indian National", 125, y + 5.5);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 23, 42);
    doc.text(`${symbol} ${formatNum(baseFee)}`, 175, y + 5.5, { align: "right" });

    y += 8.5;

    // Item 2: Optional Mini-Course (if added)
    if (paymentData.miniCourseAdded) {
        const miniCourseFee = paymentData.miniCourseAmount || (isForeign ? 300 : 5000);
        doc.setFillColor(255, 255, 255);
        doc.rect(14, y, 182, 8.5, "F");
        doc.line(14, y + 8.5, 196, y + 8.5);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(String(rowCount++), 18, y + 5.5);

        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text("Mini-Course (Optional Add-on)", 30, y + 5.5);

        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        doc.text("Add-on Tutorial", 125, y + 5.5);

        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text(`${symbol} ${formatNum(miniCourseFee)}`, 175, y + 5.5, { align: "right" });

        y += 8.5;
    }

    // Item 3: IEEE 10% Discount (if applied)
    if (paymentData.discountAmount && Number(paymentData.discountAmount) > 0) {
        doc.setFillColor(255, 255, 255);
        doc.rect(14, y, 182, 8.5, "F");
        doc.line(14, y + 8.5, 196, y + 8.5);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(String(rowCount++), 18, y + 5.5);

        doc.setFont("helvetica", "bold");
        doc.setTextColor(4, 120, 87);
        doc.text("IEEE Member 10% Discount", 30, y + 5.5);

        doc.setFont("helvetica", "normal");
        doc.setTextColor(4, 120, 87);
        doc.text("Member Benefit", 125, y + 5.5);

        doc.setFont("helvetica", "bold");
        doc.setTextColor(4, 120, 87);
        doc.text(`- ${symbol} ${formatNum(paymentData.discountAmount)}`, 175, y + 5.5, { align: "right" });

        y += 8.5;
    }

    // TOTAL ROW
    doc.setFillColor(239, 246, 255); // Blue-50
    doc.setDrawColor(59, 130, 246);  // Blue-500
    doc.rect(14, y, 182, 10, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(11, 41, 84);
    doc.text("TOTAL AMOUNT RECEIVED (NET PAID · INCL. OF GST):", 24, y + 6.5);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(11, 41, 84);
    doc.text(`${symbol} ${formatNum(paymentData.totalAmount)}`, 175, y + 6.5, { align: "right" });

    y += 15;

    // -------------------------------------------------------------
    // 5. SECTION 3: GATEWAY TRANSACTION RECORD
    // -------------------------------------------------------------
    doc.setFillColor(241, 245, 249);
    doc.rect(14, y, 182, 7, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(11, 41, 84);
    doc.text("3. PAYMENT TRANSACTION RECORD", 18, y + 5);

    y += 7;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.rect(14, y, 182, 26, "FD");

    // Gateway Key-Values (without digital signature text)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Razorpay Payment ID:", 18, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(paymentData.razorpayPaymentId || "Verified Online", 60, y + 6);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Razorpay Order ID:", 18, y + 13);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
    doc.text(paymentData.razorpayOrderId || "—", 60, y + 13);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Payment Mode:", 18, y + 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);
    doc.text("Online Payment via Razorpay Gateway", 60, y + 20);

    // Gateway Badge Box (Right side)
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(144, y + 3, 48, 20, 2, 2, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(11, 41, 84);
    doc.text("PAYMENT GATEWAY", 168, y + 8, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(4, 120, 87);
    doc.text("SUCCESS", 168, y + 14, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Razorpay Payments India", 168, y + 19, { align: "center" });

    y += 33;

    // -------------------------------------------------------------
    // 6. OFFICIAL SEAL & IMPORTANT INSTRUCTIONS
    // -------------------------------------------------------------
    // Guidelines Box (Left)
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(14, y, 120, 34, 2, 2, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(11, 41, 84);
    doc.text("IMPORTANT DELEGATE GUIDELINES:", 18, y + 6);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(71, 85, 105);
    doc.text("1. Present this digital receipt or printout at the conference helpdesk for delegate kit.", 18, y + 11.5);
    doc.text("2. Carry a valid Government Photo ID (Aadhaar / Passport) matching your registration.", 18, y + 16.5);
    doc.text("3. Official acknowledgment & tax receipt. Registration fees are inclusive of GST.", 18, y + 21.5);
    doc.text(`4. Conference GSTIN: ${gstNumber}`, 18, y + 26.5);
    doc.text("5. Email: ivec2027@gmail.com for help, inquiries, or registration assistance.", 18, y + 31.5);

    // Official Verification Seal (Right - without digital signature text)
    doc.setFillColor(240, 253, 244); // Green-50
    doc.setDrawColor(22, 163, 74);   // Green-600
    doc.setLineWidth(0.8);
    doc.roundedRect(140, y, 56, 34, 3, 3, "FD");
    doc.setLineWidth(0.2); // reset

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(22, 101, 52);
    doc.text("IVEC 2027", 168, y + 8, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(21, 128, 61);
    doc.text("OFFICIALLY PAID", 168, y + 16, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.setTextColor(22, 101, 52);
    doc.text("MTRDC (DRDO) & IEEE EDS", 168, y + 23, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Official Registration Confirmation", 168, y + 28, { align: "center" });

    // -------------------------------------------------------------
    // 7. BOTTOM FOOTER
    // -------------------------------------------------------------
    doc.setDrawColor(203, 213, 225);
    doc.line(14, 283, 196, 283);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(148, 163, 184);
    doc.text(
        "International Vacuum Electronics Conference (IVEC 2027) · Bengaluru, India · Official Registration Receipt",
        105,
        287,
        { align: "center" }
    );

    // Save PDF
    const cleanName = (delegateName || "Delegate").replace(/[^a-zA-Z0-9]/g, "_");
    const filename = `IVEC_2027_Receipt_${cleanName}_${paymentData.razorpayPaymentId || "CONFIRMED"}.pdf`;
    doc.save(filename);
};
